// 移动端模块入口文件
import { createApp } from 'vue'
import mobileRouter from './router'
import './assets/mobile.css'
import './assets/theme.css'

// 移动端组件
import MobileApp from './MobileApp.vue'
import MobileHeader from './components/MobileHeader.vue'
import MobileTabBar from './components/MobileTabBar.vue'
import MobileToast from './components/MobileToast.vue'

// 移动端页面组件
import HomePage from './views/Home/HomePage.vue'
import PurchasePage from './views/Purchase/PurchasePage.vue'
import SalesPage from './views/Sales/SalesPage.vue'
import InventoryPage from './views/Inventory/InventoryPage.vue'
import FinancePage from './views/Finance/FinancePage.vue'

// 移动端工具函数
export const mobileUtils = {
  // 检测是否为移动设备
  isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  },
  
  // 检测是否为触摸设备
  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0
  },
  
  // 获取视口尺寸
  getViewportSize() {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    }
  },
  
  // 设置视口meta标签
  setViewportMeta() {
    let viewport = document.querySelector('meta[name=viewport]')
    if (!viewport) {
      viewport = document.createElement('meta')
      viewport.name = 'viewport'
      document.head.appendChild(viewport)
    }
    viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
  },
  
  // 禁用页面缩放
  disableZoom() {
    document.addEventListener('touchstart', function(event) {
      if (event.touches.length > 1) {
        event.preventDefault()
      }
    }, { passive: false })
    
    let lastTouchEnd = 0
    document.addEventListener('touchend', function(event) {
      const now = (new Date()).getTime()
      if (now - lastTouchEnd <= 300) {
        event.preventDefault()
      }
      lastTouchEnd = now
    }, false)
  },
  
  // 添加安全区域CSS变量
  setSafeAreaVars() {
    const root = document.documentElement
    
    // 检测是否支持安全区域
    if (CSS.supports('padding: env(safe-area-inset-top)')) {
      root.style.setProperty('--safe-area-top', 'env(safe-area-inset-top)')
      root.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom)')
      root.style.setProperty('--safe-area-left', 'env(safe-area-inset-left)')
      root.style.setProperty('--safe-area-right', 'env(safe-area-inset-right)')
    } else {
      root.style.setProperty('--safe-area-top', '0px')
      root.style.setProperty('--safe-area-bottom', '0px')
      root.style.setProperty('--safe-area-left', '0px')
      root.style.setProperty('--safe-area-right', '0px')
    }
  },
  
  // 防抖函数
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  },
  
  // 节流函数
  throttle(func, limit) {
    let inThrottle
    return function() {
      const args = arguments
      const context = this
      if (!inThrottle) {
        func.apply(context, args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }
  },
  
  // 格式化金额
  formatCurrency(amount, currency = '¥') {
    if (typeof amount !== 'number') return currency + '0.0000'
    return currency + amount.toLocaleString('zh-CN', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    })
  },
  
  // 格式化日期
  formatDate(date, format = 'YYYY-MM-DD') {
    if (!date) return ''
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hour = String(d.getHours()).padStart(2, '0')
    const minute = String(d.getMinutes()).padStart(2, '0')
    const second = String(d.getSeconds()).padStart(2, '0')
    
    return format
      .replace('YYYY', year)
      .replace('MM', month)
      .replace('DD', day)
      .replace('HH', hour)
      .replace('mm', minute)
      .replace('ss', second)
  },
  
  // 复制到剪贴板
  async copyToClipboard(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return true
      } else {
        // 降级方案
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        const result = document.execCommand('copy')
        textArea.remove()
        return result
      }
    } catch (error) {
      console.error('复制失败:', error)
      return false
    }
  },
  
  // 震动反馈
  vibrate(pattern = [100]) {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern)
    }
  },
  
  // 显示Toast消息
  showToast(message, type = 'info', duration = 3000) {
    // 创建Toast元素
    const toast = document.createElement('div')
    toast.className = `mobile-toast mobile-toast-${type}`
    toast.textContent = message
    
    // 添加样式
    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '9999',
      padding: '12px 16px',
      borderRadius: '8px',
      color: 'white',
      fontSize: '14px',
      fontWeight: '500',
      maxWidth: '80%',
      textAlign: 'center',
      opacity: '0',
      transition: 'opacity 0.3s ease'
    })
    
    // 设置背景色（使用主题CSS变量）
    const rootStyles = getComputedStyle(document.documentElement)
    const colors = {
      success: rootStyles.getPropertyValue('--success-500')?.trim() || '#10b981',
      error: rootStyles.getPropertyValue('--error-500')?.trim() || '#ef4444',
      warning: rootStyles.getPropertyValue('--warning-500')?.trim() || '#f59e0b',
      info: rootStyles.getPropertyValue('--info-500')?.trim() || '#3b82f6'
    }
    toast.style.backgroundColor = colors[type] || colors.info
    
    // 添加到页面
    document.body.appendChild(toast)
    
    // 显示动画
    setTimeout(() => {
      toast.style.opacity = '1'
    }, 10)
    
    // 自动隐藏
    setTimeout(() => {
      toast.style.opacity = '0'
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast)
        }
      }, 300)
    }, duration)
  }
}

// 移动端初始化函数
export function initMobile() {
  // 设置视口
  mobileUtils.setViewportMeta()
  
  // 设置安全区域变量
  mobileUtils.setSafeAreaVars()
  
  // 禁用缩放（可选）
  if (mobileUtils.isMobile()) {
    mobileUtils.disableZoom()
  }
  
  // 添加移动端类名
  document.documentElement.classList.add('mobile-device')
  
  console.log('移动端模块初始化完成')
}

// 创建移动端应用实例
export function createMobileApp(options = {}) {
  const app = createApp(MobileApp)
  
  // 注册全局组件
  app.component('MobileHeader', MobileHeader)
  app.component('MobileTabBar', MobileTabBar)
  app.component('MobileToast', MobileToast)
  
  // 注册全局属性
  app.config.globalProperties.$mobile = mobileUtils
  
  // 使用路由
  app.use(mobileRouter)
  
  // 应用其他配置
  if (options.store) {
    app.use(options.store)
  }
  
  if (options.plugins) {
    options.plugins.forEach(plugin => {
      app.use(plugin)
    })
  }
  
  return app
}

// 导出组件
export {
  MobileApp,
  MobileHeader,
  MobileTabBar,
  MobileToast,
  HomePage,
  PurchasePage,
  SalesPage,
  InventoryPage,
  FinancePage,
  mobileRouter
}

// 默认导出
export default {
  install(app, options = {}) {
    // 初始化移动端
    initMobile()
    
    // 注册全局组件
    app.component('MobileApp', MobileApp)
    app.component('MobileHeader', MobileHeader)
    app.component('MobileTabBar', MobileTabBar)
    app.component('MobileToast', MobileToast)
    
    // 注册全局属性
    app.config.globalProperties.$mobile = mobileUtils
    
    // 添加路由
    if (options.router) {
      // 将移动端路由添加到主路由中
      mobileRouter.getRoutes().forEach(route => {
        options.router.addRoute(route)
      })
    }
  }
}